// Phase 33 · 19a — the rules behind the Dashboard overview.
//
// Kept out of the component so the honesty rules the artboard argues for are
// testable rather than asserted: a zero response time is MISSING data and must
// render '—' (the BFF sends 0 when nothing has been responded to, and '0m'
// would read as instant), a distinct breach count is never faked by summing
// the two SLA dimensions (a ticket that breaches both would be counted twice),
// and a roster with no workload figures degrades to a note rather than a
// column of confident zeros.

import type { AgentMetrics, DashboardMetrics, SlaMetrics } from '@services/analytics.service';
import type { InboxItem } from '@services/inbox.service';

/** 'Good morning' before noon, 'Good afternoon' to 18:00, then 'Good evening'. */
export function greeting(at: Date = new Date()): string {
  const h = at.getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

/** "Vikram Shah" → "Vikram". The greeting uses one name, never the full record. */
export function firstName(name?: string | null): string {
  const first = (name || '').trim().split(/\s+/)[0];
  return first || 'there';
}

/** 'Thursday 11 September' — the date the greeting line is dated by. */
export function longDate(at: Date = new Date()): string {
  return at.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/**
 * Open = new + in progress + pending. Deliberately NOT `tickets.total`, which
 * includes resolved, closed and backtracked — the card says "open tickets" and
 * has to mean it.
 */
export function openCount(t?: DashboardMetrics['tickets']): number | null {
  if (!t) return null;
  return (t.new || 0) + (t.in_progress || 0) + (t.pending || 0);
}

/**
 * Distinct tickets that breached at least one SLA dimension.
 *
 * `firstResponse.breached + resolution.breached` DOUBLE-COUNTS a ticket that
 * breached both, so the sum is never used as a fallback. A BFF build that
 * predates `breachedTickets` gets '—' and the two dimensions underneath.
 */
export function breachedTickets(s?: SlaMetrics): number | null {
  return s?.breachedTickets ?? null;
}

/** Milliseconds → seconds for `duration()`. 0 and null both mean "no data". */
export function msToSeconds(ms?: number | null): number | null {
  if (ms === null || ms === undefined || ms <= 0) return null;
  return ms / 1000;
}

export function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : Math.round((sorted[mid - 1] + sorted[mid]) / 2);
}

export interface LoadRow {
  id: string;
  name: string;
  role?: string;
  held: number;
}

/**
 * The roster, heaviest first, INCLUDING members holding nothing — an empty row
 * is a staffing fact, and filtering it out is how a roster quietly lies about
 * capacity.
 *
 * Returns null when the workload snapshot degraded (the BFF leaves
 * `openTickets` undefined on failure), so the card can say so instead of
 * drawing a column of zeros that look like idle agents.
 */
export function agentLoad(agents?: AgentMetrics[]): LoadRow[] | null {
  if (!agents || agents.length === 0) return [];
  if (agents.every((a) => a.openTickets === undefined || a.openTickets === null)) return null;
  return agents
    .map((a) => ({
      id: a.agentId,
      name: a.agentName,
      role: a.role,
      held: a.openTickets ?? 0,
    }))
    .sort((a, b) => b.held - a.held || a.name.localeCompare(b.name));
}

export interface Concentration {
  /** Display name of whoever holds the most of them, or null for unassigned. */
  holder: string | null;
  held: number;
  total: number;
}

/**
 * Who is holding the urgent work. The artboard's finding — "4 of 5 sit with one
 * agent" — is only worth printing when it is actually true of the rows shown,
 * so this reports the real split and the caller decides whether it is notable.
 */
export function concentration(rows: InboxItem[]): Concentration | null {
  if (rows.length === 0) return null;
  const counts = new Map<string | null, number>();
  for (const r of rows) {
    const key = r.assignee?.name || null;
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  let holder: string | null = null;
  let held = 0;
  for (const [key, n] of counts) {
    if (n > held) {
      holder = key;
      held = n;
    }
  }
  return { holder, held, total: rows.length };
}

/** Print the finding only when one name really does hold most of the queue. */
export function isNotable(c: Concentration | null): boolean {
  return Boolean(c && c.total >= 3 && c.held > c.total / 2);
}
